export const FORM_DATE_FORMAT_STRING = 'DD/MM/YYYY';
export const YEAR_MONTH_FIRST_DAY_FORMAT = 'YYYY-MM-01';
export const INVALID_DATE = 'Invalid date';

// Regex patterns
export const ALL_WHITESPACE_REGEX_TEST = /^\s*$/;
const TRAILING_WHITESPACE_REGEX = /\s$/;

/**
 * General-purpose reducer function for managing both summary and component states.
 * @param {Object} state - The current state.
 * @param {Object} action - The action object containing the field(s) to update.
 * @param {Object|string} action.field - The field(s) to update. Can be an object with multiple key-value pairs or a single field name.
 * @param {*} [action.value] - The new value to assign if a single field is being updated.
 * @returns {Object} The updated state.
 *   - If action.field is an object: A new state object with all fields from action.field merged with the existing state
 *   - If action.field is a string: A new state object with the single field updated to action.value
 */
export const generalReducer = (state, { field, value }) => {
  if (typeof field === 'object') {
    return { ...state, ...field };
  }

  return { ...state, [field]: value };
};

/**
 * Validates whether the given input is a valid integer.
 *
 * @param {string} input - The input value to be validated.
 * @returns {boolean} True if the input is an empty string or a valid integer, false otherwise.
 */
export const isValidInteger = (input) => {

  // Checks if the input is a number
  // Input type number usually accepts ['e', 'E', '+', '-'] as valid characters
  const numericValue = Number(input);
  // Checks if the input has a decimal point
  const hasDecimalPoint = String(input).includes('.');
  // Checks if the input has trailing whitespace
  const hasTrailingWhitespace = TRAILING_WHITESPACE_REGEX.test(input);

  return !!numericValue && !hasDecimalPoint && !hasTrailingWhitespace;
};

/**
 * @typedef {false | 0 | "" | [] | null | undefined} Falsy
 */

/**
 * Returns the provided value if it is truthy; otherwise, returns the fallback value or null if both are falsy.
 * Primarily used to reduce complexity flags raised by SonarQube.
 *
 * @template {unknown} TValue
 * @template {unknown} TFallback
 * @param {TValue} value - The primary value to be evaluated.
 * @param {TFallback} [fallback] - The fallback value to return if the primary value is falsy.
 * @returns {TValue extends Falsy 
 *   ? (TFallback extends undefined ? null : TFallback) 
 *   : TValue} The primary value if truthy, otherwise the fallback or null.
 */
export const getValueOrFallback = (value, fallback) =>
 /** @type {any} */ (value || (fallback === undefined ? null : fallback));
